import { UserType } from "../interfaces/User";

import {
  WidgetsType,
  type IWidgetDB,
  type IWidgetBrowser,
  type IWidgetTriggers,
  type IWidgetAudioVisualizer,
} from "../interfaces/Widgets";

import {
  EAudioVisualizerSource,
  type IAudioVisualizerSourceConfig,
} from "../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

/**
 * Paneles del Dashboard (sidebar) que cada perfil siembra en el primer arranque.
 * Solo los standalone (BROWSER, TRIGGERS, AUDIO_VISUALIZER): los gated
 * (GUEST/AGENT/SELFHOST) no se siembran porque sin conexión saldrían vacíos.
 *
 * Las entradas no llevan `id` (la tabla `widgets` usa ++id autoincremental) y
 * replican los payloads por defecto de los modales de alta (ver
 * ModalWidgets.hooks.ts / ModalAudioVisualizer.hooks.ts) para no divergir.
 */

const DEFAULT_VISUALIZER_SOURCES: IAudioVisualizerSourceConfig[] = [
  {
    enabled: true,
    color: "#3b82f6",
    source: EAudioVisualizerSource.MICROPHONE,
    label: "Micrófono",
  },
  {
    enabled: true,
    color: "#f59e0b",
    source: EAudioVisualizerSource.PLAYLIST,
    label: "Playlist",
  },
  {
    enabled: true,
    color: "#10b981",
    source: EAudioVisualizerSource.SYSTEM_AUDIO,
    label: "Audio del Sistema",
  },
  {
    enabled: true,
    color: "#a855f7",
    source: EAudioVisualizerSource.VIDEO_AREA,
    label: "Videos",
  },
];

const triggersWidget = (n: number): IWidgetTriggers => ({
  name: `${WidgetsType.TRIGGERS} ${n}`,
  type: WidgetsType.TRIGGERS,
  configuration: { rules: [] },
});

const browserWidget = (n: number): IWidgetBrowser => ({
  name: `${WidgetsType.BROWSER} ${n}`,
  type: WidgetsType.BROWSER,
  configuration: {
    bounds: { width: 400, height: 200, left: 0, top: 0, zIndex: 1 },
    src: "",
  },
});

const audioVisualizerWidget = (n: number): IWidgetAudioVisualizer => ({
  name: `${WidgetsType.AUDIO_VISUALIZER} ${n}`,
  type: WidgetsType.AUDIO_VISUALIZER,
  configuration: {
    sources: DEFAULT_VISUALIZER_SOURCES,
    backgroundColor: "transparent",
    barCount: 32,
    barGap: 2,
    barRadius: 2,
    smoothing: 0.8,
    fftSize: 512,
  },
});

/**
 * Devuelve los paneles por defecto para un perfil. Numeración correlativa
 * (Triggers 1, Browser 1, ...) igual que el alta manual.
 */
export const getPanelWidgetPresets = (userType: UserType): IWidgetDB[] => {
  switch (userType) {
    case UserType.Streamer:
    case UserType.Gamer:
      return [triggersWidget(1), browserWidget(1)];

    case UserType.Radio:
    case UserType.LiveConcerts:
      return [audioVisualizerWidget(1), triggersWidget(1), browserWidget(1)];

    case UserType.VirtualClasses:
      return [browserWidget(1), triggersWidget(1)];

    case UserType.LocalRecording:
    default:
      // Captura en solitario: sin paneles en vivo por defecto.
      return [];
  }
};
